import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, MapPin, FileText, Share2, Settings, Trash2, Loader2,
  Filter, Layers, MousePointerClick, Pencil, Sparkles, Camera, History, X, Ruler, ScanLine, MessageSquare, Send, BarChart3, Minus, Maximize2, Eye, EyeOff, Crosshair, ThumbsUp, ThumbsDown, CheckCircle2, XCircle, Copy,
} from "lucide-react";
import api, { API, fileUrl } from "@/lib/api";
import { SEV_META, STATUS_META, TYPE_META, prettyType, ASSET_TYPES, assetUnit } from "@/lib/defects";
import MatterportViewer from "@/components/MatterportViewer";
import SplatMode from "@/components/SplatMode";
import { FloorPlan } from "@/components/FloorPlan";
import DefectDialog from "@/components/DefectDialog";
import InspectionSummary from "@/components/InspectionSummary";
import AiStats from "@/components/AiStats";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function SevDot({ sev }) {
  const m = SEV_META[sev] || SEV_META.medium;
  return <span className="w-2 h-2 rounded-full inline-block" style={{ background: m.color }} />;
}

export default function ProjectWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const viewerRef = useRef(null);

  const [project, setProject] = useState(null);
  const [levels, setLevels] = useState([]);
  const [activeLevel, setActiveLevel] = useState(null);
  const [defects, setDefects] = useState([]);
  const [sdkKey, setSdkKey] = useState("");
  const [filterSev, setFilterSev] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [placement, setPlacement] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState({ done: 0, total: 0, found: 0 });
  const [captureBusy, setCaptureBusy] = useState(false);
  const [initialCapture, setInitialCapture] = useState(null);
  const [selectedDefect, setSelectedDefect] = useState(null);
  const [aimed, setAimed] = useState(null);
  const [boxPos, setBoxPos] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [statsKey, setStatsKey] = useState(0);
  const [showStats, setShowStats] = useState(false);
  const [cardMin, setCardMin] = useState(false);
  const [boxVisible, setBoxVisible] = useState(false);
  const [pinArming, setPinArming] = useState(false);
  const [rejectReason, setRejectReason] = useState(null);
  const [correctionNote, setCorrectionNote] = useState("");
  const [debugLog, setDebugLog] = useState([]);
  const [showDebug, setShowDebug] = useState(false);
  const [ptrHit, setPtrHit] = useState(null);
  const [lastPin, setLastPin] = useState(null);
  const [pinHistory, setPinHistory] = useState([]);
  const [pinScreens, setPinScreens] = useState([]);
  const pushDbg = (m) => setDebugLog((l) => [...l.slice(-9), `${new Date().toISOString().slice(11, 19)} ${m}`]);
  const [calib, setCalib] = useState(() => { try { return JSON.parse(localStorage.getItem("scanBoxCalib")) || { yaw: 0, pitch: 0 }; } catch (e) { return { yaw: 0, pitch: 0 }; } });
  const [summaryKey, setSummaryKey] = useState(0);

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsForm, setSettingsForm] = useState({});
  const [shareOpen, setShareOpen] = useState(false);
  const [newLevel, setNewLevel] = useState("");
  const [coverBusy, setCoverBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [savingInspection, setSavingInspection] = useState(false);
  const [plans, setPlans] = useState([]);
  const [comments, setComments] = useState([]);
  const [defTitles, setDefTitles] = useState({});
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [replyDraft, setReplyDraft] = useState({});
  const [allDefects, setAllDefects] = useState([]);
  const [viewMode, setViewMode] = useState("scan"); // scan | splat

  // Track the aimed defect's direction and project it onto the live scan each frame
  // so the bounding box stays anchored to the defect as the camera moves.
  useEffect(() => {
    if (viewMode !== "scan" || !aimed) { setBoxPos(null); return; }
    if (aimed.anchor3d?.position) { setBoxPos(null); return; } // native 3D pin handles it
    const b = (aimed.boxes || [])[0];
    if (!b) { setBoxPos(null); return; }
    const u = b.x + b.w / 2, v = b.y + b.h / 2;
    const anchor = aimed.anchor;
    const targetYaw = anchor ? anchor.yaw : (u - 0.5) * 360 + (calib.yaw || 0);
    const targetPitch = anchor ? anchor.pitch : (0.5 - v) * 180 + (calib.pitch || 0);
    const FOV_H = 100, FOV_V = 60; // larger assumed FOV => smaller, better-fitting boxes
    const norm = (a) => (((a + 180) % 360) + 360) % 360 - 180;
    let raf;
    const tick = () => {
      const pose = viewerRef.current?.getCameraPose?.();
      if (pose && pose.rotation) {
        const dYaw = norm(targetYaw - pose.rotation.y);
        const dPitch = targetPitch - pose.rotation.x;
        const leftPct = 50 + (dYaw / (FOV_H / 2)) * 50;
        const topPct = 50 - (dPitch / (FOV_V / 2)) * 50;
        const wPct = Math.min(Math.max((b.w * 360) / FOV_H, 0.03), 0.9) * 100;
        const hPct = Math.min(Math.max((b.h * 180) / FOV_V, 0.03), 0.9) * 100;
        const visible = Math.abs(dYaw) < FOV_H * 0.6 && Math.abs(dPitch) < FOV_V * 0.7;
        setBoxPos({ leftPct, topPct, wPct, hPct, visible });
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [aimed, viewMode, calib]);

  // Restore native 3D pins in the scan for every defect that was pinned.
  useEffect(() => {
    if (viewMode !== "scan") return;
    const pinned = defects.filter((d) => d.anchor3d?.position);
    let tries = 0;
    const t = setInterval(() => {
      const v = viewerRef.current;
      if (v?.isReady?.()) { v.syncPins?.(pinned); clearInterval(t); }
      if (++tries > 25) clearInterval(t);
    }, 600);
    return () => clearInterval(t);
  }, [defects, viewMode]);

  // Live read of the 3D point under the cursor (fast while arming a pin).
  useEffect(() => {
    if (viewMode !== "scan" || (!showDebug && !pinArming)) return;
    const t = setInterval(() => { setPtrHit(viewerRef.current?.getPointerHit?.() || null); }, pinArming ? 100 : 500);
    return () => clearInterval(t);
  }, [showDebug, viewMode, pinArming]);

  // Project every pinned defect's 3D world point to screen pixels each tick so
  // we can draw our OWN anchored marker (Matterport's native tag doesn't render here).
  useEffect(() => {
    if (viewMode !== "scan") { setPinScreens([]); return; }
    const pinned = defects.filter((d) => d.anchor3d?.position);
    if (!pinned.length) { setPinScreens([]); return; }
    const t = setInterval(() => {
      const v = viewerRef.current;
      const out = [];
      pinned.forEach((d, i) => {
        const s = v?.worldToScreen?.(d.anchor3d.position);
        if (s && s.w) {
          const visible = s.x >= -20 && s.x <= s.w + 20 && s.y >= -20 && s.y <= s.h + 20 && (s.z == null || (s.z >= 0 && s.z <= 1));
          out.push({ id: d.id, title: d.title, severity: d.severity, xPct: (s.x / s.w) * 100, yPct: (s.y / s.h) * 100, visible, index: i + 1, approx: !!d.anchor3d.approx, review_status: d.review_status, source: d.source });
        }
      });
      setPinScreens(out);
    }, 60);
    return () => clearInterval(t);
  }, [defects, viewMode]);

  const selectDefectFromMarker = (id) => {
    const d = defects.find((x) => x.id === id) || allDefects.find((x) => x.id === id);
    if (d) { setSelectedDefect(d); setAimed(d); setCardMin(false); }
  };

  const PLAN_STATUS_META = {
    not_started: { label: "Not started", color: "#94A3B8", bg: "rgba(148,163,184,0.14)" },
    in_progress: { label: "In progress", color: "#C5A24A", bg: "rgba(212,168,75,0.14)" },
    blocked: { label: "Blocked", color: "#EA580C", bg: "rgba(234,88,12,0.14)" },
    complete: { label: "Complete", color: "#10B981", bg: "rgba(16,185,129,0.14)" },
  };

  const loadAll = async () => {
    const [p, lv] = await Promise.all([api.get(`/projects/${id}`), api.get(`/projects/${id}/levels`)]);
    setProject(p.data);
    setSettingsForm(p.data);
    setLevels(lv.data);
    setActiveLevel((cur) => cur || (lv.data[0]?.id ?? null));
    api.get(`/projects/${id}/action-plans`).then((r) => setPlans(r.data.action_plans || [])).catch(() => {});
    Promise.all([api.get(`/projects/${id}/comments`), api.get(`/defects`, { params: { project_id: id } })]).then(([c, dd]) => {
      setComments(c.data.comments || []);
      setAllDefects(dd.data || []);
      const m = {}; (dd.data || []).forEach((d) => { m[d.id] = d.title; }); setDefTitles(m);
    }).catch(() => {});
  };

  const loadComments = () => api.get(`/projects/${id}/comments`).then((r) => setComments(r.data.comments || [])).catch(() => {});

  const loadDefects = async () => {
    const params = { project_id: id };
    if (activeLevel) params.level_id = activeLevel;
    const { data } = await api.get("/defects", { params });
    setDefects(data);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { api.get("/settings").then((r) => setSdkKey(r.data.matterport_sdk_key || "")).catch(() => {}); }, []);
  useEffect(() => { if (activeLevel !== null) loadDefects(); /* eslint-disable-next-line */ }, [activeLevel]);

  const filtered = useMemo(() => defects.filter((d) =>
    (filterSev === "all" || d.severity === filterSev) &&
    (filterStatus === "all" || d.status === filterStatus)
  ), [defects, filterSev, filterStatus]);

  // Reviewed findings sorted ascending by created_at — matches the PDF/DOCX report numbering (dnum)
  const reviewedSorted = useMemo(() => {
    const rev = allDefects.filter((d) => ["approved", "rejected", "duplicate"].includes(d.review_status));
    return [...rev].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  }, [allDefects]);
  const dnum = useMemo(() => {
    const m = {};
    reviewedSorted.forEach((d, i) => { m[d.id] = i + 1; });
    return m;
  }, [reviewedSorted]);
  const planDefects = useMemo(
    () => (activeLevel ? reviewedSorted.filter((d) => d.level_id === activeLevel) : reviewedSorted),
    [reviewedSorted, activeLevel]
  );

  const openNew = () => {
    setPlacement(null);
    setEditing(null);
    setDialogOpen(true);
  };

  const onSaved = async () => { await loadDefects(); await loadAll(); setSummaryKey((k) => k + 1); };

  const saveAnchor = async () => {
    const p = viewerRef.current?.getCameraPose?.();
    if (!p?.rotation || !selectedDefect) return toast.error("Move the scan so the box sits on the defect, then lock");
    // Freeze the box where it currently is on screen, expressed in camera coords
    // (must match the FOV used by the tracking effect) so it doesn't jump on lock.
    const FOV_H = 100, FOV_V = 60;
    const offX = boxPos ? (boxPos.leftPct - 50) / 50 : 0;
    const offY = boxPos ? (boxPos.topPct - 50) / 50 : 0;
    const yaw = p.rotation.y + offX * (FOV_H / 2);
    const pitch = p.rotation.x - offY * (FOV_V / 2);
    try {
      const { data } = await api.patch(`/defects/${selectedDefect.id}/anchor`, { yaw, pitch });
      setSelectedDefect((s) => (s && s.id === data.id ? { ...s, ...data } : s));
      setAimed((a) => (a && a.id === data.id ? { ...a, ...data } : a));
      toast.success("Box locked in place — it'll return here every time");
    } catch (e) { toast.error("Could not lock the box"); }
  };
  const saveCalib = (c) => { setCalib(c); try { localStorage.setItem("scanBoxCalib", JSON.stringify(c)); } catch (e) {} };
  const nudge = (k, d) => {
    if (aimed?.anchor) {
      const na = k === "yaw" ? { yaw: aimed.anchor.yaw + d, pitch: aimed.anchor.pitch } : { yaw: aimed.anchor.yaw, pitch: aimed.anchor.pitch + d };
      api.patch(`/defects/${aimed.id}/anchor`, na).then(({ data }) => {
        setAimed((a) => (a && a.id === data.id ? { ...a, ...data } : a));
        setSelectedDefect((s) => (s && s.id === data.id ? { ...s, ...data } : s));
      }).catch(() => {});
    } else {
      saveCalib({ ...calib, [k]: (calib[k] || 0) + d });
    }
  };
  const resetCalib = () => saveCalib({ yaw: 0, pitch: 0 });

  // Pin the selected defect to a true 3D world point via a native Matterport tag.
  const pinToScan = async () => {
    const v = viewerRef.current;
    if (!v?.isReady?.() || !selectedDefect) return toast.error("Open the 3D scan and select a defect first");
    // Remove any previous tag for this defect so we never leave a stale pin behind.
    if (selectedDefect.anchor3d?.sid) { try { await v.removePin?.(selectedDefect.anchor3d.sid); } catch (e) {} }
    pushDbg(`pin: start pressed for "${selectedDefect.title}"`);
    setPinArming(true);
    const hit = await v.captureNextClickHit?.(() => toast.error("You clicked the scan — that moves the camera. Re-hover the defect and press ENTER (don't click)."));
    setPinArming(false);
    pushDbg(`pin: capture returned ${hit ? JSON.stringify(hit).slice(0, 90) : "null"}`);
    if (!hit || hit.cancelled) return;
    if (!hit.position) return toast.error("No surface clicked — click right on the defect in the scan");
    const n = hit.normal || { x: 0, y: 1, z: 0 };
    const res = await v.pinDefect?.({
      label: selectedDefect.title,
      description: prettyType(selectedDefect.defect_type),
      severity: selectedDefect.severity,
      position: hit.position,
      stemVector: { x: n.x * 0.18, y: n.y * 0.18, z: n.z * 0.18 },
      floorId: hit.floorId,
    });
    pushDbg(`pin: Tag.add -> ${res ? JSON.stringify(res).slice(0, 90) : "null"}`);
    if (!res?.sid) return toast.error("Couldn't place the pin — try again");
    setLastPin(res.position);
    const recordActual = (attempt = 0) => {
      const tags = viewerRef.current?.getTags?.() || [];
      const placed = tags.find((t) => t.sid === res.sid);
      const ap = placed?.anchorPosition;
      if (ap) {
        pushDbg(`ACTUAL(mp)=${ap.x.toFixed(2)},${ap.y.toFixed(2)},${ap.z.toFixed(2)}`);
        setPinHistory((h) => [{ title: selectedDefect.title, intended: res.position, actual: ap }, ...h].slice(0, 4));
      } else if (attempt < 6) {
        setTimeout(() => recordActual(attempt + 1), 300);
      } else {
        pushDbg(`ACTUAL(mp): tag ${res.sid} NOT in collection (tags=${tags.length})`);
        setPinHistory((h) => [{ title: selectedDefect.title, intended: res.position, actual: null }, ...h].slice(0, 4));
      }
    };
    recordActual();
    try {
      const { data } = await api.patch(`/defects/${selectedDefect.id}/anchor3d`, { position: res.position, stemVector: res.stemVector, floorId: res.floorId, sid: res.sid });
      setSelectedDefect((s) => (s && s.id === data.id ? { ...s, ...data } : s));
      setAimed((a) => (a && a.id === data.id ? { ...a, ...data } : a));
      await loadDefects();
      const p = res.position;
      toast.success(`Pinned at ${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)} — saved to this defect`);
    } catch (e) { toast.error("Could not save the pin"); }
  };
  const cancelPin = () => { viewerRef.current?.cancelPinCapture?.(); setPinArming(false); };
  const unpin = async () => {
    const v = viewerRef.current;
    if (!selectedDefect) return;
    if (selectedDefect.anchor3d?.sid) { try { await v?.removePin?.(selectedDefect.anchor3d.sid); } catch (e) {} }
    try {
      const { data } = await api.patch(`/defects/${selectedDefect.id}/anchor3d`, { position: null });
      setSelectedDefect((s) => (s && s.id === data.id ? { ...s, ...data } : s));
      setAimed((a) => (a && a.id === data.id ? { ...a, ...data } : a));
      await loadDefects();
      toast.success("Pin removed");
    } catch (e) { toast.error("Could not remove pin"); }
  };

  const reviewDefect = async (d, status, reason = null, note = null) => {
    try {
      const { data } = await api.patch(`/defects/${d.id}/review`, { review_status: status, reject_reason: reason, correction_note: note });
      setSelectedDefect((s) => (s && s.id === d.id ? { ...s, ...data } : s));
      await loadDefects();
      setStatsKey((k) => k + 1);
      if (status === "approved") toast.success("Marked correct — helps train the AI");
      else if (status === "rejected") toast.success("Feedback saved — helps train the AI");
      else if (status === "duplicate") toast.success("Marked as duplicate — won't double-count");
    } catch (e) { toast.error("Could not save review"); }
  };
  const toggleMissed = async (d) => {
    try {
      const { data } = await api.patch(`/defects/${d.id}/ai-missed`, { ai_missed: !d.ai_missed });
      setSelectedDefect((s) => (s && s.id === d.id ? { ...s, ...data } : s));
      await loadDefects();
      setStatsKey((k) => k + 1);
    } catch (e) { toast.error("Could not update"); }
  };

  const runAutoScan = async () => {
    const v = viewerRef.current;
    if (!v?.isReady?.()) return toast.error("Connect a Matterport scan first (Model ID + SDK key)");
    const sweeps = v.listSweeps() || [];
    if (!sweeps.length) return toast.error("No sweeps found in this scan yet");
    setScanning(true);
    setScanProgress({ done: 0, total: sweeps.length, found: 0 });
    let found = 0;
    const uploadFrame = async (b64) => {
      const uri = b64.startsWith("data:") ? b64 : `data:image/jpeg;base64,${b64}`;
      const blob = await (await fetch(uri)).blob();
      const fd = new FormData();
      fd.append("file", new File([blob], `autoscan-${Date.now()}.jpg`, { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.path;
    };
    for (let i = 0; i < sweeps.length; i++) {
      const s = sweeps[i];
      const b64 = await v.captureSweep(s.id);
      if (b64) {
        try {
          const { data } = await api.post("/ai/scan-image", { image_base64: b64, asset_type: project?.asset_type || "parking" });
          const dets = data.detections || [];
          // Save the exact frame the AI analysed so each defect shows WHERE it is.
          let framePath = null;
          if (dets.length) { try { framePath = await uploadFrame(b64); } catch (e) { framePath = null; } }
          for (let di = 0; di < dets.length; di++) {
            const det = dets[di];
            const bx = det.box;
            const boxes = (Array.isArray(bx) && bx.length === 4 && framePath)
              ? [{ photo: framePath, x: bx[0], y: bx[1], w: bx[2], h: bx[3], defect_type: det.defect_type, severity: det.severity }]
              : [];
            await api.post("/defects", {
              project_id: id, level_id: activeLevel,
              title: det.title || "Detected defect",
              defect_type: det.defect_type, severity: det.severity, status: "open",
              location_note: `AI auto-scan · position ${i + 1}`, measurement: "",
              photos: framePath ? [framePath] : [], boxes,
              position: s.position || null, ai_analysis: det, sweep_id: s.id,
              source: "ai", review_status: "pending",
            });
            found++;
          }
        } catch (e) { /* skip sweep on error */ }
      }
      setScanProgress({ done: i + 1, total: sweeps.length, found });
    }
    setScanning(false);
    await loadDefects();
    await loadAll();
    setSummaryKey((k) => k + 1);
    toast.success(`Auto-scan complete — ${found} defect(s) flagged for review`);
  };

  const captureFromScan = async () => {
    const v = viewerRef.current;
    if (!v?.isReady?.()) return toast.error("Connect the 3D scan first (Model ID + SDK key)");
    setCaptureBusy(true);
    try {
      const uri = await v.captureScreenshot();
      if (!uri) { toast.error("Could not capture the view — is the scan loaded?"); return; }
      const blob = await (await fetch(uri)).blob();
      const fd = new FormData();
      fd.append("file", new File([blob], `scan-capture-${Date.now()}.jpg`, { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const pl = v.getPlacement?.() || { position: null, normal: null };
      setPlacement(pl);
      setEditing(null);
      setInitialCapture(data.path);
      setDialogOpen(true);
    } catch (e) {
      toast.error("Capture failed");
    } finally {
      setCaptureBusy(false);
    }
  };

  const removeDefect = async (dId) => {    await api.delete(`/defects/${dId}`);
    setDefects((prev) => prev.filter((d) => d.id !== dId));
    setSelectedDefect((cur) => (cur && cur.id === dId ? null : cur));
    setSummaryKey((k) => k + 1);
    toast.success("Defect deleted");
  };

  const saveSettings = async () => {
    await api.put(`/projects/${id}`, settingsForm);
    await loadAll();
    setSettingsOpen(false);
    toast.success("Project updated");
  };

  const addLevel = async () => {
    if (!newLevel.trim()) return;
    const { data } = await api.post(`/projects/${id}/levels`, { name: newLevel, order: levels.length });
    setLevels((prev) => [...prev, { ...data, defect_count: 0 }]);
    setNewLevel("");
  };

  const openReport = () => window.open(`${API}/projects/${id}/report.pdf`, "_blank");
  const openReportDocx = () => window.open(`${API}/projects/${id}/report.docx`, "_blank");
  const shareLink = project ? `${window.location.origin}/share/${project.share_token}` : "";

  const regeneratePin = async () => {
    const { data } = await api.post(`/projects/${id}/share-pin`);
    setProject((p) => ({ ...p, share_pin: data.share_pin }));
    toast.success("New access code generated");
  };

  const unreadComments = comments.filter((c) => c.role === "client" && !c.read).length;
  const groupByDefect = comments.reduce((acc, c) => { (acc[c.defect_id] = acc[c.defect_id] || []).push(c); return acc; }, {});
  const planDefIds = new Set(plans.filter((p) => p.defect_id).map((p) => p.defect_id));
  const totalDefectsAll = Object.keys(defTitles).length || defects.length;
  const donePlans = plans.filter((p) => p.status === "complete").length;
  const remPct = totalDefectsAll ? Math.round((planDefIds.size / totalDefectsAll) * 100) : 0;

  const markCommentsRead = async () => { await api.post(`/projects/${id}/comments/mark-read`); loadComments(); };
  const sendReply = async (defectId) => {
    const body = (replyDraft[defectId] || "").trim(); if (!body) return;
    await api.post(`/projects/${id}/defects/${defectId}/comments`, { body });
    setReplyDraft((d) => ({ ...d, [defectId]: "" })); loadComments(); toast.success("Reply sent");
  };

  const uploadCover = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCoverBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettingsForm((prev) => ({ ...prev, cover_photo: data.path }));
      toast.success("Cover photo set — save to apply");
    } catch { toast.error("Upload failed"); } finally { setCoverBusy(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLogoBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setSettingsForm((prev) => ({ ...prev, client_logo: data.path }));
      toast.success("Client logo set — save to apply");
    } catch { toast.error("Upload failed"); } finally { setLogoBusy(false); }
  };

  const saveInspection = async () => {
    setSavingInspection(true);
    try {
      const { data } = await api.post(`/projects/${id}/inspections`, {});
      setSummaryKey((k) => k + 1);
      toast.success(`Inspection snapshot saved (${data.defect_count} findings). Future reports will show what changed since today.`);
    } catch { toast.error("Could not save inspection"); } finally { setSavingInspection(false); }
  };

  if (!project) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const unit = assetUnit(project.asset_type);

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden bg-background text-foreground flex flex-col">
      {/* Top bar */}
      <div className="h-16 border-b border-white/10 flex items-center justify-between px-6 shrink-0 bg-[#0A0A0A]">
        <div className="flex items-center gap-4 min-w-0">
          <Button variant="ghost" size="icon" data-testid="back-button" onClick={() => navigate("/dashboard")} className="text-muted-foreground hover:text-white shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="min-w-0">
            <h1 className="font-display font-bold tracking-tight truncate">{project.name}</h1>
            <p className="text-xs text-muted-foreground truncate">{project.address || "No address"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="settings-button" onClick={() => setSettingsOpen(true)} className="gap-2 border-white/10 bg-[#121212] hover:bg-[#1E1E1E]">
            <Settings className="w-4 h-4" /> <span className="hidden sm:inline">Settings</span>
          </Button>
          <Button variant="outline" size="sm" data-testid="comments-button" onClick={() => setCommentsOpen(true)} className="gap-2 border-white/10 bg-[#121212] hover:bg-[#1E1E1E] relative">
            <MessageSquare className="w-4 h-4" /> <span className="hidden sm:inline">Inbox</span>
            {unreadComments > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#C5A24A] text-black text-[10px] font-bold flex items-center justify-center" data-testid="unread-badge">{unreadComments}</span>}
          </Button>
          <Button variant="outline" size="sm" data-testid="share-button" onClick={() => setShareOpen(true)} className="gap-2 border-white/10 bg-[#121212] hover:bg-[#1E1E1E]">
            <Share2 className="w-4 h-4" /> <span className="hidden sm:inline">Share</span>
          </Button>
          <Button variant="outline" size="sm" data-testid="save-inspection-button" onClick={saveInspection} disabled={savingInspection} title="Snapshot current findings so future reports can show what changed" className="gap-2 border-white/10 bg-[#121212] hover:bg-[#1E1E1E]">
            {savingInspection ? <Loader2 className="w-4 h-4 animate-spin" /> : <History className="w-4 h-4" />} <span className="hidden sm:inline">Save Inspection</span>
          </Button>
          <Button size="sm" data-testid="report-button" onClick={openReport} className="gap-2 bg-[#C5A24A] hover:bg-[#A8863A] text-white transition-colors">
            <FileText className="w-4 h-4" /> Report
          </Button>
          <Button variant="outline" size="sm" data-testid="report-docx-button" onClick={openReportDocx} title="Download the report as an editable Word document" className="gap-2 border-white/10 bg-[#121212] hover:bg-[#1E1E1E]">
            <FileText className="w-4 h-4" /> <span className="hidden sm:inline">Word</span>
          </Button>
        </div>
      </div>

      <div className="flex-1 grid lg:grid-cols-[1fr_400px] min-h-0">
        {/* Viewer + levels */}
        <div className="flex flex-col min-h-0 border-r border-white/10">
          <div className="flex items-center gap-2 px-4 h-12 border-b border-white/10 overflow-x-auto shrink-0 bg-[#0A0A0A]">
            <Layers className="w-4 h-4 text-muted-foreground shrink-0" />
            {levels.map((lv) => (
              <button
                key={lv.id}
                data-testid={`level-tab-${lv.id}`}
                onClick={() => setActiveLevel(lv.id)}
                className={`text-sm px-3 py-1.5 rounded-full whitespace-nowrap transition-colors border ${activeLevel === lv.id ? "bg-[#1E1E1E] border-white/20 text-white" : "border-transparent text-muted-foreground hover:text-white"}`}
              >
                {lv.name} <span className="text-xs opacity-60">{lv.defect_count}</span>
              </button>
            ))}
            <div className="flex items-center gap-1 shrink-0">
              <Input value={newLevel} onChange={(e) => setNewLevel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addLevel()} placeholder={`Add ${unit.one.toLowerCase()}`} data-testid="new-level-input" className="h-8 w-32 bg-[#121212] border-white/10 text-xs" />
              <Button size="icon" variant="ghost" onClick={addLevel} data-testid="add-level-button" className="h-8 w-8 text-muted-foreground hover:text-white"><Plus className="w-4 h-4" /></Button>
            </div>
          </div>

          <div className="relative flex-1 min-h-[360px]">
            <MatterportViewer
              ref={viewerRef}
              modelId={project.matterport_model_id}
              sdkKey={sdkKey}
              onDebug={pushDbg}
            />
            {viewMode === "splat" && <SplatMode markers={allDefects} />}
            {viewMode === "plan" && (
              <FloorPlan
                defects={planDefects}
                numberOf={dnum}
                levelName={levels.find((l) => l.id === activeLevel)?.name}
                unitLabel={unit.one}
                selectedId={selectedDefect?.id}
                onSelect={(d) => setSelectedDefect(d)}
                onCaptureFloorplan={(wps) => viewerRef.current?.captureFloorplan?.(wps)}
              />
            )}
            {viewMode === "scan" && pinScreens.some((p) => p.visible) && (
              <div className="absolute inset-0 z-[14] pointer-events-none" data-testid="pin-markers">
                {pinScreens.filter((p) => p.visible).map((p) => (
                  <button
                    key={p.id}
                    data-testid={`pin-marker-${p.id}`}
                    onClick={() => selectDefectFromMarker(p.id)}
                    className={`absolute -translate-x-1/2 -translate-y-full pointer-events-auto flex flex-col items-center ${p.approx ? "opacity-80" : ""}`}
                    style={{ left: `${p.xPct}%`, top: `${p.yPct}%` }}
                    title={p.approx ? `${p.title} (approximate — verify)` : p.title}
                  >
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold text-black shadow-lg whitespace-nowrap mb-0.5 max-w-[190px] truncate ${p.source === "ai" && p.review_status === "rejected" ? "line-through" : ""}`} style={{ background: (SEV_META[p.severity] || SEV_META.medium).color }}>{p.source === "ai" && p.review_status === "approved" ? "✓ " : p.source === "ai" && p.review_status === "rejected" ? "✗ " : p.source === "ai" && p.review_status === "duplicate" ? "⧉ " : ""}{p.approx ? "~ " : ""}{p.index}. {p.title}</span>
                    <span className={`w-4 h-4 rounded-full shadow-lg ${p.approx ? "border-2 border-dashed border-white" : "border-2 border-white"}`} style={{ background: (SEV_META[p.severity] || SEV_META.medium).color }} />
                  </button>
                ))}
              </div>
            )}
            {viewMode === "scan" && selectedDefect && project.matterport_model_id && (
              <div className="absolute top-16 left-4 z-[15] pointer-events-auto" data-testid="scan-pin-panel">
                {selectedDefect.anchor3d?.position ? (
                  <div className="flex flex-col gap-1 bg-black/80 border border-[#C5A24A]/40 rounded-lg px-3 py-2 backdrop-blur max-w-[248px]" data-testid="pinned-state">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-3.5 h-3.5 text-[#C5A24A]" />
                      <span className="text-[11px] text-white/90 font-medium truncate">Pinned: {selectedDefect.title}</span>
                      <button onClick={unpin} data-testid="unpin-defect-scan" className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-white/10 hover:bg-white/20 text-white/70">Remove</button>
                    </div>
                    <div className="text-[10px] font-mono text-[#C5A24A]/90" data-testid="pinned-coords">{selectedDefect.anchor3d.position.x.toFixed(2)}, {selectedDefect.anchor3d.position.y.toFixed(2)}, {selectedDefect.anchor3d.position.z.toFixed(2)}</div>
                    {selectedDefect.anchor3d.approx && <div className="text-[9px] text-white/50">approximate (AI) — press Start pinning to set the exact spot</div>}
                  </div>
                ) : (
                  <div className="flex flex-col gap-1.5 bg-black/80 border border-white/15 rounded-lg p-2.5 backdrop-blur max-w-[248px]">
                    <p className="text-[11px] text-white/90 font-semibold flex items-center gap-1.5"><Crosshair className="w-3.5 h-3.5 text-[#C5A24A]" /> Pin "{selectedDefect.title}"</p>
                    {pinArming ? (
                      <>
                        <p className="text-[10px] text-[#C5A24A] leading-snug font-medium">Hover the defect, then press ENTER (don't click). Esc to cancel.</p>
                        <div className="rounded bg-[#C5A24A]/15 border border-[#C5A24A]/40 px-2 py-1.5" data-testid="pin-live-readout">
                          <div className="text-[9px] uppercase tracking-wider text-[#C5A24A]/80">will pin here (live)</div>
                          <div className="text-[12px] font-bold text-white">{ptrHit?.position ? `${ptrHit.position.x.toFixed(2)}, ${ptrHit.position.y.toFixed(2)}, ${ptrHit.position.z.toFixed(2)}` : "move cursor onto the defect…"}</div>
                        </div>
                        <button onClick={cancelPin} data-testid="cancel-pin" className="text-[11px] px-2.5 py-1.5 rounded-md bg-white/10 text-white/80 hover:bg-white/20 flex items-center justify-center gap-1.5 transition-colors">Cancel</button>
                      </>
                    ) : (
                      <>
                        <p className="text-[10px] text-white/55 leading-snug">Press Start, then hover the defect and press ENTER. Don't click — clicking moves the camera.</p>
                        <button onClick={pinToScan} data-testid="pin-to-scan-scan" className="text-[11px] px-2.5 py-1.5 rounded-md bg-[#C5A24A] text-black font-semibold hover:bg-[#A8863A] flex items-center justify-center gap-1.5 transition-colors">
                          <MapPin className="w-3.5 h-3.5" /> Start pinning
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
            {viewMode === "scan" && showDebug && (
              <div className="absolute bottom-20 right-4 z-[40] w-80 max-h-64 overflow-auto bg-black/90 border border-[#C5A24A]/40 rounded-lg p-2 text-[10px] font-mono text-white/80 pointer-events-auto" data-testid="pin-debug">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[#C5A24A] font-bold">PIN DEBUG</span>
                  <button onClick={() => setShowDebug(false)} className="text-white/50 hover:text-white">hide ✕</button>
                </div>
                <div className="text-white/60">cursor 3D hit: {ptrHit?.position ? `${ptrHit.position.x.toFixed(2)}, ${ptrHit.position.y.toFixed(2)}, ${ptrHit.position.z.toFixed(2)}` : "— (move mouse over the scan)"}</div>
                <div className="text-[#C5A24A]">intended pin: {lastPin ? `${lastPin.x.toFixed(2)}, ${lastPin.y.toFixed(2)}, ${lastPin.z.toFixed(2)}` : "—"}</div>
                <div className="border-t border-white/10 mt-1 pt-1">
                  <div className="text-white/40 mb-0.5">recent pins (sent → matterport actual):</div>
                  {pinHistory.length ? pinHistory.map((p, i) => (
                    <div key={i} className="break-all text-white/70">
                      {(p.title || "").slice(0, 12)}: {p.intended ? `${p.intended.x.toFixed(1)},${p.intended.y.toFixed(1)},${p.intended.z.toFixed(1)}` : "—"} → {p.actual ? `${p.actual.x.toFixed(1)},${p.actual.y.toFixed(1)},${p.actual.z.toFixed(1)}` : "NOT FOUND"}
                    </div>
                  )) : <div className="text-white/30">none yet</div>}
                </div>
                <div className="border-t border-white/10 mt-1 pt-1 space-y-0.5">
                  {debugLog.length ? debugLog.map((l, i) => <div key={i} className="break-all">{l}</div>) : <div className="text-white/40">no events yet…</div>}
                </div>
              </div>
            )}
            {viewMode === "scan" && !showDebug && (
              <button onClick={() => setShowDebug(true)} data-testid="show-debug" className="absolute bottom-20 right-4 z-[40] text-[10px] px-2 py-1 rounded-full bg-black/70 border border-white/15 text-white/70 hover:text-white">debug</button>
            )}
            <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1 bg-black/75 border border-white/10 rounded-full p-1 backdrop-blur" data-testid="view-mode-toggle">
              <button onClick={() => setViewMode("scan")} data-testid="view-scan" className={`text-[11px] px-3 py-1 rounded-full transition-colors ${viewMode === "scan" ? "bg-[#C5A24A] text-black font-semibold" : "text-white hover:bg-white/10"}`}>Scan</button>
              <button disabled title="Upload a Gaussian Splat to this project to enable" data-testid="view-splat" className="text-[11px] px-3 py-1 rounded-full text-muted-foreground/50 cursor-not-allowed">Gaussian Splat</button>
              <button onClick={() => setViewMode("splat")} data-testid="view-open-local" className={`text-[11px] px-3 py-1 rounded-full transition-colors ${viewMode === "splat" ? "bg-[#C5A24A] text-black font-semibold" : "text-white hover:bg-white/10"}`}>Open Local Splat</button>
              <button onClick={() => setViewMode("plan")} data-testid="view-floor-plan" className={`text-[11px] px-3 py-1 rounded-full transition-colors ${viewMode === "plan" ? "bg-[#C5A24A] text-black font-semibold" : "text-white hover:bg-white/10"}`}>Floor Plan</button>
            </div>
            <div className="absolute top-0 left-0 z-10 flex items-center gap-2 pl-3 pr-4 py-3 rounded-br-lg bg-[#0A0A0A] min-w-[150px]" data-testid="level-badge">
              <span className="label-caps text-[#C5A24A]">{unit.one}</span>
              <span className="font-display font-bold text-sm truncate">{levels.find((l) => l.id === activeLevel)?.name || "—"}</span>
            </div>
            {(viewMode === "scan" || viewMode === "plan") && selectedDefect && cardMin && (
              <div className="absolute bottom-4 left-4 z-20 flex items-center gap-2 bg-[#F8F9FA] text-[#09090B] rounded-full shadow-2xl border border-zinc-200 pl-3 pr-1.5 py-1.5" data-testid="floating-defect-card-min">
                <ScanLine className="w-4 h-4" style={{ color: "#A8863A" }} />
                <span className="text-xs font-semibold truncate max-w-[160px]">{selectedDefect.title}</span>
                <button onClick={() => setCardMin(false)} data-testid="expand-floating-card" className="w-6 h-6 rounded-full hover:bg-zinc-200 flex items-center justify-center" title="Expand"><Maximize2 className="w-3.5 h-3.5" /></button>
                <button onClick={() => { setSelectedDefect(null); setAimed(null); }} className="w-6 h-6 rounded-full hover:bg-zinc-200 flex items-center justify-center" title="Close"><X className="w-3.5 h-3.5" /></button>
              </div>
            )}
            {(viewMode === "scan" || viewMode === "plan") && selectedDefect && !cardMin && (
              <div className="absolute bottom-4 left-4 z-20 w-96 bg-[#F8F9FA] text-[#09090B] rounded-lg shadow-2xl border border-zinc-200 max-h-[calc(100vh-7rem)] overflow-y-auto" data-testid="floating-defect-card">
                {selectedDefect.photos?.[0] && (selectedDefect.boxes || [])[0] && (() => {
                  const b = selectedDefect.boxes[0];
                  const denomW = Math.max(1 - b.w, 0.0001), denomH = Math.max(1 - b.h, 0.0001);
                  return (
                    <div className="relative h-48 bg-zinc-900" data-testid="detection-zoom"
                      style={{ backgroundImage: `url(${fileUrl(selectedDefect.photos[0])})`, backgroundRepeat: "no-repeat", backgroundSize: `${100 / Math.max(b.w, 0.0001)}% ${100 / Math.max(b.h, 0.0001)}%`, backgroundPosition: `${(b.x / denomW) * 100}% ${(b.y / denomH) * 100}%` }}>
                      <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-[#C5A24A] text-black text-[10px] font-bold uppercase tracking-wider">AI detection · close-up</span>
                    </div>
                  );
                })()}
                <div className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <ScanLine className="w-4 h-4 shrink-0" style={{ color: "#A8863A" }} />
                      <span className="font-display font-bold truncate">{selectedDefect.title}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button onClick={() => setCardMin(true)} data-testid="minimize-floating-card" className="text-zinc-400 hover:text-zinc-700" title="Minimise"><Minus className="w-4 h-4" /></button>
                      <button onClick={() => { setSelectedDefect(null); setAimed(null); }} data-testid="close-floating-card" className="text-zinc-400 hover:text-zinc-700"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <span className="inline-block mt-2 px-3 py-1 rounded text-[10px] font-bold uppercase tracking-widest" style={{ background: (SEV_META[selectedDefect.severity] || SEV_META.medium).bg, color: (SEV_META[selectedDefect.severity] || SEV_META.medium).color }}>{(SEV_META[selectedDefect.severity] || SEV_META.medium).label}</span>
                  <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm border-t border-zinc-200 mt-3 pt-3">
                    <span className="text-zinc-500">Type</span><span className="text-right font-medium">{prettyType(selectedDefect.defect_type)}</span>
                    <span className="text-zinc-500">Status</span><span className="text-right font-medium">{(STATUS_META[selectedDefect.status] || STATUS_META.open).label}</span>
                    <span className="text-zinc-500">Location</span><span className="text-right font-medium truncate">{selectedDefect.location_note || "—"}</span>
                    <span className="text-zinc-500">Measurement</span><span className="text-right font-medium">{selectedDefect.measurement || "—"}</span>
                  </div>
                </div>
                {selectedDefect.photos?.[0] && (
                  <div className="relative bg-zinc-900">
                    <img src={fileUrl(selectedDefect.photos[0])} alt={selectedDefect.title} className="w-full block" />
                    {(selectedDefect.boxes || []).map((b, i) => (
                      <div key={b.id || i} className="absolute border-2" style={{ left: `${b.x * 100}%`, top: `${b.y * 100}%`, width: `${b.w * 100}%`, height: `${b.h * 100}%`, borderColor: "#C5A24A", boxShadow: "0 0 0 1px rgba(0,0,0,0.5)" }}>
                        <span className="absolute -top-2.5 -left-2.5 w-5 h-5 rounded-full bg-[#C5A24A] text-black text-[10px] font-bold flex items-center justify-center border border-black/40">{i + 1}</span>
                      </div>
                    ))}
                    {selectedDefect.measurement && (
                      <span className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/70 px-2 py-1 rounded text-xs font-mono" style={{ color: "#C5A24A" }}><Ruler className="w-3 h-3" /> {selectedDefect.measurement}</span>
                    )}
                  </div>
                )}
                {(selectedDefect.sweep_id || selectedDefect.anchor3d?.position) && (
                  <button
                    data-testid="goto-pano-button"
                    onClick={async () => {
                      const b = (selectedDefect.boxes || [])[0];
                      const lookAtPos = selectedDefect.anchor3d?.position || null;
                      let aim = null;
                      if (selectedDefect.anchor) {
                        aim = { yaw: selectedDefect.anchor.yaw, pitch: selectedDefect.anchor.pitch };
                      } else if (b) {
                        const u = b.x + b.w / 2, v = b.y + b.h / 2;
                        aim = { yaw: (u - 0.5) * 360 + (calib.yaw || 0), pitch: (0.5 - v) * 180 + (calib.pitch || 0) };
                      }
                      let ok = false;
                      if (selectedDefect.sweep_id) {
                        ok = await viewerRef.current?.gotoSweep?.(selectedDefect.sweep_id, aim, lookAtPos);
                      } else if (lookAtPos) {
                        ok = await viewerRef.current?.lookAt?.(lookAtPos);
                      }
                      if (!ok) toast.error("Could not move to that spot — is the scan loaded?");
                      else { setAimed(selectedDefect); setCardMin(true); }
                    }}
                    className="m-3 inline-flex items-center gap-2 h-9 px-4 rounded-md text-sm font-semibold bg-[#C5A24A] text-black hover:bg-[#A8863A] transition-colors"
                  >
                    <MapPin className="w-4 h-4" /> {selectedDefect.anchor3d?.position ? "Go to the pinned spot" : "Go to this spot in the 3D scan"}
                  </button>
                )}
                {project.matterport_model_id && (
                  <div className="px-4 py-3 border-t border-zinc-200" data-testid="pin-controls">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Location in 3D scan</p>
                    {selectedDefect.anchor3d?.position ? (
                      <div className="flex items-center gap-2 text-xs font-semibold text-green-600">
                        <MapPin className="w-3.5 h-3.5" /> Pinned in the 3D scan
                        <button onClick={unpin} data-testid="unpin-defect" className="ml-auto text-[10px] underline text-zinc-400 font-normal">remove pin</button>
                      </div>
                    ) : (
                      <>
                        <button onClick={pinToScan} disabled={pinArming} data-testid="pin-to-scan" className="w-full h-9 rounded-md bg-[#C5A24A] text-black text-xs font-semibold hover:bg-[#A8863A] disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
                          <Crosshair className="w-3.5 h-3.5" /> {pinArming ? "Hover defect + press ENTER…" : "Pin to this spot in the scan"}
                        </button>
                        <p className="text-[10px] text-zinc-400 mt-1.5 leading-snug">{pinArming ? "Hover your cursor on the defect and press ENTER (don't click — clicking moves the camera). Esc to cancel." : "Press this, then hover the defect and press ENTER. It anchors in true 3D and never drifts."}</p>
                      </>
                    )}
                  </div>
                )}
                {selectedDefect.source === "ai" && (
                  <div className="px-4 py-3 border-t border-zinc-200" data-testid="ai-review-bar">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Did the AI get this right? (trains the AI)</p>
                    {selectedDefect.review_status === "approved" ? (
                      <div className="flex items-center gap-2 text-xs font-semibold text-green-600">✓ Confirmed correct
                        <button onClick={() => reviewDefect(selectedDefect, "pending")} className="ml-auto text-[10px] underline text-zinc-400 font-normal">change</button>
                      </div>
                    ) : selectedDefect.review_status === "rejected" ? (
                      <div className="text-xs font-semibold text-red-600">
                        <div className="flex items-center gap-2">✗ False positive{selectedDefect.reject_reason ? ` · ${selectedDefect.reject_reason.replace(/_/g, " ")}` : ""}
                          <button onClick={() => reviewDefect(selectedDefect, "pending")} className="ml-auto text-[10px] underline text-zinc-400 font-normal">change</button>
                        </div>
                        {selectedDefect.correction_note && <p className="mt-1 text-[11px] font-normal text-zinc-600" data-testid="correction-display">Actually: {selectedDefect.correction_note}</p>}
                      </div>
                    ) : selectedDefect.review_status === "duplicate" ? (
                      <div className="flex items-center gap-2 text-xs font-semibold text-blue-600"><Copy className="w-3.5 h-3.5" /> Duplicate — seen in another scan
                        <button onClick={() => reviewDefect(selectedDefect, "pending")} className="ml-auto text-[10px] underline text-zinc-400 font-normal">change</button>
                      </div>
                    ) : rejectingId === selectedDefect.id ? (
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-1.5">
                          {[["not_a_defect", "Not a defect"], ["wrong_type", "Wrong type"], ["wrong_severity", "Wrong severity"], ["other", "Other"]].map(([k, l]) => (
                            <button key={k} data-testid={`reject-reason-${k}`} onClick={() => setRejectReason(k)} className={`text-[11px] px-2 py-1 rounded border transition-colors ${rejectReason === k ? "border-red-500 bg-red-50 text-red-700" : "border-zinc-300 hover:bg-zinc-100"}`}>{l}</button>
                          ))}
                        </div>
                        <textarea data-testid="correction-note" value={correctionNote} onChange={(e) => setCorrectionNote(e.target.value)} placeholder="What is it actually? (optional — e.g. it's spalling, not a crack)" className="w-full text-[11px] rounded border border-zinc-300 px-2 py-1.5 resize-none h-14 text-zinc-800" />
                        <div className="flex gap-2">
                          <button data-testid="save-reject" disabled={!rejectReason} onClick={() => { reviewDefect(selectedDefect, "rejected", rejectReason, correctionNote.trim() || null); setRejectingId(null); }} className="flex-1 h-8 rounded bg-red-600 text-white text-xs font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors">Save feedback</button>
                          <button onClick={() => setRejectingId(null)} className="px-3 h-8 rounded text-xs text-zinc-500 hover:bg-zinc-100">cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <button data-testid="approve-defect" onClick={() => reviewDefect(selectedDefect, "approved")} className="flex-1 h-9 rounded bg-green-600 text-white text-xs font-semibold hover:bg-green-700 transition-colors flex items-center justify-center gap-1.5"><ThumbsUp className="w-4 h-4" /> Correct</button>
                          <button data-testid="reject-defect" onClick={() => { setRejectReason(null); setCorrectionNote(""); setRejectingId(selectedDefect.id); }} className="flex-1 h-9 rounded bg-zinc-200 text-zinc-800 text-xs font-semibold hover:bg-zinc-300 transition-colors flex items-center justify-center gap-1.5"><ThumbsDown className="w-4 h-4" /> Wrong</button>
                        </div>
                        <button data-testid="duplicate-defect" onClick={() => reviewDefect(selectedDefect, "duplicate")} className="w-full h-8 rounded bg-blue-50 text-blue-700 border border-blue-200 text-xs font-semibold hover:bg-blue-100 transition-colors flex items-center justify-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Duplicate (same defect in another scan)</button>
                      </div>
                    )}
                  </div>
                )}
                {selectedDefect.source !== "ai" && (
                  <div className="px-4 py-3 border-t border-zinc-200">
                    <label className="flex items-center gap-2 text-xs cursor-pointer text-zinc-700" data-testid="ai-missed-toggle">
                      <input type="checkbox" checked={!!selectedDefect.ai_missed} onChange={() => toggleMissed(selectedDefect)} />
                      <span>AI missed this defect (counts toward recall)</span>
                    </label>
                  </div>
                )}
                {(selectedDefect.boxes || []).length > 0 && (
                  <div className="px-4 py-3 border-t border-zinc-200 space-y-1" data-testid="floating-card-boxes">
                    {selectedDefect.boxes.map((b, i) => (
                      <div key={b.id || i} className="flex items-center gap-2 text-xs">
                        <span className="w-4 h-4 rounded-full bg-[#C5A24A] text-black text-[9px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>
                        <span className="font-medium text-zinc-800">{prettyType(b.defect_type)}</span>
                        <span className="ml-auto" style={{ color: (SEV_META[b.severity] || SEV_META.medium).color }}>{(SEV_META[b.severity] || SEV_META.medium).label}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="absolute bottom-4 right-4 z-10 flex items-center gap-2">
              <Button data-testid="auto-scan-button" onClick={runAutoScan} disabled={scanning} className="gap-2 bg-[#121212] border border-[#C5A24A]/40 text-[#D8BE86] hover:bg-[#C5A24A] hover:text-black shadow-lg transition-colors">
                {scanning ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                AI Auto-Scan
              </Button>
              <Button data-testid="capture-from-scan-button" onClick={captureFromScan} disabled={captureBusy} className="gap-2 bg-[#121212] border border-white/15 text-white hover:bg-[#1E1E1E] shadow-lg transition-colors">
                {captureBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
                Capture from Scan
              </Button>
              <Button data-testid="add-defect-button" onClick={openNew} className="gap-2 bg-[#C5A24A] hover:bg-[#A8863A] text-black shadow-lg transition-colors">
                <Plus className="w-4 h-4" /> Add Defect
              </Button>
            </div>
            {scanning && (
              <div className="absolute inset-0 z-20 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center" data-testid="scan-overlay">
                <Loader2 className="w-8 h-8 animate-spin text-[#C5A24A] mb-4" />
                <p className="font-display font-semibold">AI is scanning your model…</p>
                <p className="text-sm text-muted-foreground mt-1">Position {scanProgress.done} / {scanProgress.total} · {scanProgress.found} defect(s) found</p>
                <div className="w-64 h-1.5 bg-white/10 rounded-full mt-4 overflow-hidden">
                  <div className="h-full bg-[#C5A24A] transition-all" style={{ width: `${scanProgress.total ? (scanProgress.done / scanProgress.total) * 100 : 0}%` }} />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Defect list */}
        <div className="flex flex-col min-h-0 bg-[#0A0A0A]">
          <InspectionSummary projectId={id} refreshKey={summaryKey} onSaveBaseline={saveInspection} />
          {plans.length > 0 && (
            <div className="p-4 border-b border-white/10 shrink-0" data-testid="workspace-remediation-summary">
              <div className="flex items-center justify-between">
                <p className="label-caps text-muted-foreground">Remediation</p>
                <p className="text-xs text-muted-foreground">{planDefIds.size}/{totalDefectsAll} planned · {donePlans}/{plans.length} done</p>
              </div>
              <div className="mt-2 h-1.5 rounded-full bg-[#121212] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${remPct}%`, background: "#C5A24A" }} /></div>
            </div>
          )}
          <div className="p-4 border-b border-white/10 shrink-0">
            <div className="flex items-center justify-between mb-3">
              <p className="font-display font-semibold text-sm flex items-center gap-2"><MapPin className="w-4 h-4 text-[#C5A24A]" /> Defects <span className="text-muted-foreground">({filtered.length})</span></p>
              <button data-testid="open-ai-stats" onClick={() => setShowStats(true)} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-full border border-white/15 text-muted-foreground hover:text-white hover:border-white/30 transition-colors"><BarChart3 className="w-3.5 h-3.5" /> AI accuracy</button>
            </div>
            <Dialog open={showStats} onOpenChange={setShowStats}>
              <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><BarChart3 className="w-4 h-4 text-[#C5A24A]" /> AI detection accuracy · this project</DialogTitle></DialogHeader>
                <AiStats projectId={id} refreshKey={statsKey} />
              </DialogContent>
            </Dialog>
            <div className="grid grid-cols-2 gap-2">
              <Select value={filterSev} onValueChange={setFilterSev}>
                <SelectTrigger data-testid="filter-severity" className="h-8 bg-[#121212] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10">
                  <SelectItem value="all">All severities</SelectItem>
                  {Object.keys(SEV_META).map((k) => <SelectItem key={k} value={k}>{SEV_META[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger data-testid="filter-status" className="h-8 bg-[#121212] border-white/10 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10">
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.keys(STATUS_META).map((k) => <SelectItem key={k} value={k}>{STATUS_META[k].label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {filtered.length === 0 ? (
              <div className="text-center py-16 px-6">
                <MousePointerClick className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">No defects on this {unit.one.toLowerCase()} yet. Hit <span className="text-white">Capture from Scan</span> to grab a frame from the 3D scan and box the defect.</p>
              </div>
            ) : filtered.map((d) => {
              const TypeIcon = TYPE_META[d.defect_type]?.icon || MapPin;
              return (
                <div key={d.id} data-testid={`defect-item-${d.id}`} onClick={() => setSelectedDefect(d)} className={`group border bg-[#121212] rounded p-3 transition-colors cursor-pointer ${selectedDefect?.id === d.id ? "border-[#C5A24A]" : "border-white/10 hover:border-white/25"}`}>
                  <div className="flex gap-3">
                    {d.photos?.[0] ? (
                      <img src={fileUrl(d.photos[0])} alt={d.title} className="w-14 h-14 rounded object-cover shrink-0 border border-white/10" />
                    ) : (
                      <div className="w-14 h-14 rounded bg-[#1E1E1E] flex items-center justify-center shrink-0"><TypeIcon className="w-5 h-5 text-muted-foreground" /></div>
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium truncate flex items-center gap-1 ${d.source === "ai" && d.review_status === "rejected" ? "line-through text-white/40" : ""}`}>
                          {d.source === "ai" && d.review_status === "approved" && <CheckCircle2 className="w-3.5 h-3.5 text-[#34C759] shrink-0" data-testid={`defect-approved-${d.id}`} />}
                          {d.source === "ai" && d.review_status === "rejected" && <XCircle className="w-3.5 h-3.5 text-[#FF3B30] shrink-0" data-testid={`defect-rejected-${d.id}`} />}
                          {d.source === "ai" && d.review_status === "duplicate" && <Copy className="w-3.5 h-3.5 text-blue-400 shrink-0" data-testid={`defect-duplicate-${d.id}`} />}
                          {d.title}
                        </p>
                        <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button data-testid={`edit-defect-${d.id}`} onClick={(e) => { e.stopPropagation(); setEditing(d); setDialogOpen(true); }} className="p-1 text-muted-foreground hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                          <button data-testid={`delete-defect-${d.id}`} onClick={(e) => { e.stopPropagation(); removeDefect(d.id); }} className="p-1 text-muted-foreground hover:text-[#FF3B30]"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{prettyType(d.defect_type)}{d.location_note ? ` · ${d.location_note}` : ""}</p>
                      <div className="flex items-center gap-1.5 mt-2">
                        <Badge className="gap-1 text-[10px] border-0" style={{ background: (SEV_META[d.severity] || SEV_META.medium).bg, color: (SEV_META[d.severity] || SEV_META.medium).color }}>
                          <SevDot sev={d.severity} /> {(SEV_META[d.severity] || SEV_META.medium).label}
                        </Badge>
                        <Badge variant="outline" className="text-[10px] border-white/15" style={{ color: (STATUS_META[d.status] || STATUS_META.open).color }}>{(STATUS_META[d.status] || STATUS_META.open).label}</Badge>
                        {d.anchor3d?.position ? (
                          <span className="text-[10px] text-[#C5A24A] flex items-center gap-0.5" data-testid={`defect-pinned-${d.id}`}><MapPin className="w-3 h-3" /> Pinned</span>
                        ) : (
                          <span className="text-[10px] text-white/35" data-testid={`defect-unpinned-${d.id}`}>Not pinned</span>
                        )}
                        {d.source === "ai" && d.review_status === "duplicate" && (
                          <span className="text-[10px] text-blue-400 flex items-center gap-0.5" data-testid={`defect-dup-badge-${d.id}`}><Copy className="w-3 h-3" /> Duplicate</span>
                        )}
                        {d.source === "ai" && d.review_status !== "approved" && d.review_status !== "rejected" && d.review_status !== "duplicate" && (
                          <span className="text-[10px] text-[#FFCC00] font-semibold" data-testid={`defect-review-${d.id}`}>● Needs review</span>
                        )}
                        {(() => { const pl = plans.find((x) => x.defect_id === d.id); const m = pl && (PLAN_STATUS_META[pl.status] || PLAN_STATUS_META.not_started); return pl ? (<Badge className="text-[10px] border-0" style={{ background: m.bg, color: m.color }} data-testid={`defect-plan-badge-${d.id}`}>Plan · {m.label}</Badge>) : null; })()}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <DefectDialog
        open={dialogOpen}
        onOpenChange={(v) => { setDialogOpen(v); if (!v) setInitialCapture(null); }}
        projectId={id}
        levelId={activeLevel}
        placement={placement}
        existing={editing}
        initialCapture={initialCapture}
        assetType={project?.asset_type || "parking"}
        onSaved={onSaved}
      />

      {/* Settings */}
      <Dialog open={commentsOpen} onOpenChange={(o) => { setCommentsOpen(o); if (!o && unreadComments > 0) markCommentsRead(); }}>
        <DialogContent className="bg-[#121212] border-white/10 text-foreground sm:max-w-lg flex flex-col max-h-[85vh]">
          <DialogHeader className="shrink-0"><DialogTitle className="font-display">Client questions</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto space-y-4 py-1 pr-1">
            {comments.length === 0 && <p className="text-sm text-muted-foreground">No questions yet. When a client asks a question on the shared report, it appears here.</p>}
            {Object.entries(groupByDefect).map(([defectId, list]) => (
              <div key={defectId} className="border border-white/10 rounded p-3" data-testid={`inbox-thread-${defectId}`}>
                <p className="text-xs font-medium text-[#C5A24A]">{defTitles[defectId] || "Defect"}</p>
                <div className="mt-2 space-y-2">
                  {list.map((c) => (
                    <div key={c.id} className={`text-sm rounded p-2 ${c.role === "zentara" ? "bg-[#1a1710] border border-[#C5A24A]/30" : "bg-[#0A0A0A] border border-white/10"}`}>
                      <p className="text-[11px] text-muted-foreground">{c.role === "zentara" ? "You" : (c.author_name || "Client")}{c.author_email ? ` · ${c.author_email}` : ""} · {(c.created_at || "").slice(0, 10)}</p>
                      <p className="mt-0.5">{c.body}</p>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <input value={replyDraft[defectId] || ""} onChange={(e) => setReplyDraft((d) => ({ ...d, [defectId]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && sendReply(defectId)} placeholder="Reply…" className="flex-1 bg-[#0A0A0A] border border-white/10 rounded px-2 py-1.5 text-sm" data-testid={`reply-input-${defectId}`} />
                  <Button size="sm" onClick={() => sendReply(defectId)} className="bg-[#C5A24A] hover:bg-[#A8863A] text-black" data-testid={`reply-send-${defectId}`}><Send className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
          {unreadComments > 0 && <DialogFooter className="shrink-0"><Button variant="outline" onClick={markCommentsRead} className="border-white/10 bg-[#0A0A0A] text-xs" data-testid="mark-read-button">Mark all read</Button></DialogFooter>}
        </DialogContent>
      </Dialog>

      {/* Settings */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-foreground sm:max-w-lg flex flex-col max-h-[90vh]">
          <DialogHeader className="shrink-0"><DialogTitle className="font-display">Project Settings</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2 flex-1 overflow-y-auto pr-1 -mr-1">
            <div><Label className="label-caps text-muted-foreground">Name</Label><Input data-testid="settings-name-input" value={settingsForm.name || ""} onChange={(e) => setSettingsForm({ ...settingsForm, name: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" /></div>
            <div><Label className="label-caps text-muted-foreground">Address</Label><Input value={settingsForm.address || ""} onChange={(e) => setSettingsForm({ ...settingsForm, address: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps text-muted-foreground">Job number</Label><Input data-testid="settings-job-input" value={settingsForm.job_number || ""} onChange={(e) => setSettingsForm({ ...settingsForm, job_number: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" /></div>
              <div><Label className="label-caps text-muted-foreground">Client</Label><Input data-testid="settings-client-input" value={settingsForm.client || ""} onChange={(e) => setSettingsForm({ ...settingsForm, client: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" /></div>
            </div>
            <div><Label className="label-caps text-muted-foreground">Contact person</Label><Input data-testid="settings-contact-name-input" value={settingsForm.contact_name || ""} onChange={(e) => setSettingsForm({ ...settingsForm, contact_name: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="label-caps text-muted-foreground">Contact email</Label><Input data-testid="settings-contact-email-input" value={settingsForm.contact_email || ""} onChange={(e) => setSettingsForm({ ...settingsForm, contact_email: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" /></div>
              <div><Label className="label-caps text-muted-foreground">Contact phone</Label><Input data-testid="settings-contact-phone-input" value={settingsForm.contact_phone || ""} onChange={(e) => setSettingsForm({ ...settingsForm, contact_phone: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10" /></div>
            </div>
            <div><Label className="label-caps text-muted-foreground">Matterport Model ID</Label><Input data-testid="settings-model-input" value={settingsForm.matterport_model_id || ""} onChange={(e) => setSettingsForm({ ...settingsForm, matterport_model_id: e.target.value })} className="mt-1.5 bg-[#0A0A0A] border-white/10 font-mono text-xs" placeholder="e.g. SxQL3iGyoDo" /></div>
            <div>
              <Label className="label-caps text-muted-foreground">Asset type</Label>
              <Select value={settingsForm.asset_type || "parking"} onValueChange={(v) => setSettingsForm({ ...settingsForm, asset_type: v })}>
                <SelectTrigger data-testid="settings-asset-type" className="mt-1.5 bg-[#0A0A0A] border-white/10"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1E1E] border-white/10">
                  {Object.keys(ASSET_TYPES).map((k) => <SelectItem key={k} value={k}>{ASSET_TYPES[k]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Report cover photo</Label>
              <div className="mt-1.5 flex items-center gap-3">
                {settingsForm.cover_photo ? (
                  <img src={fileUrl(settingsForm.cover_photo)} alt="cover" className="w-28 h-16 object-cover rounded border border-white/10" data-testid="cover-preview" />
                ) : (
                  <div className="w-28 h-16 rounded border border-dashed border-white/15 flex items-center justify-center text-[10px] text-muted-foreground">No photo</div>
                )}
                <label className="text-xs px-3 py-1.5 rounded bg-[#0A0A0A] border border-white/10 cursor-pointer hover:bg-[#1E1E1E]" data-testid="cover-upload-label">
                  {coverBusy ? "Uploading…" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadCover} data-testid="cover-upload-input" />
                </label>
                {settingsForm.cover_photo && (
                  <button type="button" onClick={() => setSettingsForm((p) => ({ ...p, cover_photo: "" }))} className="text-xs text-muted-foreground hover:text-[#FF3B30]" data-testid="cover-remove">Remove</button>
                )}
              </div>
            </div>
            <div>
              <Label className="label-caps text-muted-foreground">Client logo</Label>
              <div className="mt-1.5 flex items-center gap-3">
                {settingsForm.client_logo ? (
                  <img src={fileUrl(settingsForm.client_logo)} alt="client logo" className="w-28 h-16 object-contain rounded border border-white/10 bg-white p-1" data-testid="logo-preview" />
                ) : (
                  <div className="w-28 h-16 rounded border border-dashed border-white/15 flex items-center justify-center text-[10px] text-muted-foreground">No logo</div>
                )}
                <label className="text-xs px-3 py-1.5 rounded bg-[#0A0A0A] border border-white/10 cursor-pointer hover:bg-[#1E1E1E]" data-testid="logo-upload-label">
                  {logoBusy ? "Uploading…" : "Upload"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="logo-upload-input" />
                </label>
                {settingsForm.client_logo && (
                  <button type="button" onClick={() => setSettingsForm((p) => ({ ...p, client_logo: "" }))} className="text-xs text-muted-foreground hover:text-[#FF3B30]" data-testid="logo-remove">Remove</button>
                )}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">Shown beside the ZENTARA logo on the PDF cover.</p>
            </div>
            <p className="text-xs text-muted-foreground">The Model ID (the <span className="text-white">m=</span> code from your scan's share URL) embeds this property's scan. Your SDK key is set once under <span className="text-white">Settings</span> and applies to every project.</p>
          </div>
          <DialogFooter className="shrink-0"><Button data-testid="settings-save-button" onClick={saveSettings} className="bg-[#C5A24A] hover:bg-[#A8863A] text-white">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share */}
      <Dialog open={shareOpen} onOpenChange={setShareOpen}>
        <DialogContent className="bg-[#121212] border-white/10 text-foreground sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Share Inspection Report</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">Anyone with this link can view a read-only report and download the PDF. To create or update remediation action plans, they also need the access code below.</p>
            <div className="flex gap-2">
              <Input readOnly value={shareLink} data-testid="share-link-input" className="bg-[#0A0A0A] border-white/10 font-mono text-xs" />
              <Button data-testid="copy-share-button" onClick={() => { navigator.clipboard.writeText(shareLink); toast.success("Link copied"); }} className="bg-[#C5A24A] hover:bg-[#A8863A] text-white shrink-0">Copy</Button>
            </div>
            <div className="pt-3 border-t border-white/10">
              <p className="label-caps text-muted-foreground">Client access code</p>
              <p className="text-xs text-muted-foreground mt-1">Give this to your client so they can add action plans, assign owners, set dates and post photo progress updates on the report.</p>
              <div className="flex gap-2 mt-2 items-center">
                <span className="font-mono text-lg tracking-[0.3em] text-[#C5A24A] bg-[#0A0A0A] border border-white/10 rounded px-4 py-1.5" data-testid="share-pin">{project?.share_pin || "—"}</span>
                <Button variant="outline" onClick={regeneratePin} className="border-white/10 bg-[#0A0A0A] hover:bg-[#1E1E1E] text-xs" data-testid="regenerate-pin-button">Regenerate</Button>
              </div>
            </div>
            <Button variant="outline" onClick={() => window.open(shareLink, "_blank")} className="w-full border-white/10 bg-[#0A0A0A] hover:bg-[#1E1E1E]">Preview shared view</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
